/**
 * Schüler, the third level (spec 10.1): one row per attempt — topic, score of 5, date —
 * expandable to the five questions with the chosen and the correct answer.
 *
 * An attempt made before the topic was rewritten shows only its score: the rewrite deleted the
 * questions and options, and `attempt_answer` went with them (spec 4.5). The API sends
 * `answers: null` for exactly that case, and saying so is better than an empty list.
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { authedJson } from "../shared/api.js";
import type { StudentAttempt, StudentDetail } from "../shared/api-types.js";
import { germanMessage } from "../shared/errors.js";
import { Empty, ErrorLine, Loading, Panel, TButton, formatDateTime } from "./ui.js";

export function StudentScreen() {
  const { id = "" } = useParams();
  const [expanded, setExpanded] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["teacher", "student", id],
    queryFn: () => authedJson<StudentDetail>("/teacher/students/" + id, { subjectType: "teacher" }),
  });

  if (detail.isPending) return <Loading />;
  if (detail.isError || !detail.data) return <ErrorLine>{germanMessage(detail.error)}</ErrorLine>;

  const { student, attempts } = detail.data;

  return (
    <>
      <nav className="mb-3 text-[14px]">
        <Link to="/lehrer" className="text-[var(--color-accent)] no-underline hover:underline">
          Klassen
        </Link>
        <span className="text-[var(--color-muted)]"> / </span>
        <Link
          to={"/lehrer/klassen/" + student.classId}
          className="text-[var(--color-accent)] no-underline hover:underline"
        >
          Klasse
        </Link>
        <span className="text-[var(--color-muted)]"> / {student.username}</span>
      </nav>
      <h1 className="mt-0 mb-5 text-[26px] font-bold">{student.username}</h1>

      <Panel title={attempts.length === 1 ? "1 Durchgang" : attempts.length + " Durchgänge"}>
        {attempts.length === 0 ? (
          <Empty>Noch kein Thema abgeschlossen.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="t-table">
              <thead>
                <tr>
                  <th scope="col">Thema</th>
                  <th scope="col">Punkte</th>
                  <th scope="col">Datum</th>
                  <th scope="col">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((attempt) => (
                  <AttemptRows
                    key={attempt.id}
                    attempt={attempt}
                    open={expanded === attempt.id}
                    onToggle={() =>
                      setExpanded((current) => (current === attempt.id ? null : attempt.id))
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

function AttemptRows({
  attempt,
  open,
  onToggle,
}: {
  attempt: StudentAttempt;
  open: boolean;
  onToggle: () => void;
}) {
  const learned = attempt.score >= 4;
  return (
    <>
      <tr>
        <td className="font-semibold">{attempt.topic.title}</td>
        <td className="t-num">
          <span className={learned ? "text-[var(--color-ok)] font-bold" : ""}>
            {attempt.score} von 5
          </span>
        </td>
        <td>{formatDateTime(attempt.completedAt)}</td>
        <td className="text-right">
          {attempt.answers === null ? (
            <span className="text-[13px] text-[var(--color-muted)]">
              Nur Punktzahl (Thema wurde umgeschrieben)
            </span>
          ) : (
            <TButton variant="quiet" onClick={onToggle} aria-expanded={open}>
              {open ? "Fragen ausblenden" : "Fragen anzeigen"}
            </TButton>
          )}
        </td>
      </tr>
      {open && attempt.answers && (
        <tr>
          <td colSpan={4} className="bg-[#fafaf9]">
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {attempt.answers.map((answer) => (
                <li key={answer.position} className="flex flex-col gap-1">
                  <span className="text-[14px] font-semibold">
                    {answer.position}. {answer.prompt}
                  </span>
                  <span className="text-[14px]">
                    <span className="text-[var(--color-muted)]">Gewählt: </span>
                    <span
                      className={
                        answer.isCorrect
                          ? "font-semibold text-[var(--color-ok)]"
                          : "font-semibold text-[var(--color-danger)]"
                      }
                    >
                      {answer.chosen}
                    </span>
                    {!answer.isCorrect && (
                      <>
                        <span className="text-[var(--color-muted)]"> · Richtig wäre: </span>
                        <span className="font-semibold text-[var(--color-ok)]">
                          {answer.correct}
                        </span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </td>
        </tr>
      )}
    </>
  );
}
